import React from 'react';
import { X, Users, GitBranch } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * Detail modal for a single impact-path node ("ajatus") shown when a member node
 * is clicked in the pivot points view. Extracted verbatim from PivotPointsScreen.
 *
 * @param {object|null} node     the node to display (null = nothing rendered)
 * @param {function}    onClose  called on overlay/close-button click
 */
export default function MemberNodeModal({ node, onClose }) {
  const { t } = useTranslation();
  if (!node) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1100,
          cursor: 'pointer'
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          padding: '24px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
          zIndex: 1101,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: 600 }}>
            {t('pivotPoints.memberModal.title')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Node text (main content) */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          borderLeft: '4px solid #9C27B0'
        }}>
          <p style={{ margin: 0, color: 'white', fontSize: '1rem', lineHeight: 1.6 }}>
            {node.text}
          </p>
        </div>

        {/* Metadata */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {node.participant && (
            <div style={{
              background: 'rgba(33, 150, 243, 0.2)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              borderRadius: '8px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Users size={14} style={{ color: '#64B5F6' }} />
              <span style={{ color: '#64B5F6', fontSize: '0.85rem' }}>
                {node.participant}
              </span>
            </div>
          )}
          {node.depth !== undefined && (
            <div style={{
              background: 'rgba(156, 39, 176, 0.2)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              borderRadius: '8px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <GitBranch size={14} style={{ color: '#BA68C8' }} />
              <span style={{ color: '#BA68C8', fontSize: '0.85rem' }}>
                {t('pivotPoints.common.level', { depth: node.depth })}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
